/**
 * Authentication service for API access
 * Manages login, token storage, and token refresh
 */

import { getConfig } from './config';

// Token storage keys
const ACCESS_TOKEN_KEY = 'api_access_token';
const REFRESH_TOKEN_KEY = 'api_refresh_token';
const TOKEN_EXPIRY_KEY = 'api_token_expiry';

/**
 * Decode JWT token to get expiry time
 * @param {string} token - JWT token
 * @returns {number|null} Expiry timestamp in seconds or null if invalid
 */
const decodeTokenExpiry = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        
        const payload = JSON.parse(jsonPayload);
        return payload.exp || null;
    } catch (error) {
        console.error('Error decoding token:', error);
        return null;
    }
};

/**
 * Check if token is expired or about to expire (within 1 minute)
 * @param {string} token - JWT token
 * @returns {boolean} True if expired or about to expire
 */
const isTokenExpiredOrExpiring = (token) => {
    if (!token) return true;
    
    const expiry = decodeTokenExpiry(token);
    if (!expiry) return false; // Can't determine, assume valid
    
    const now = Math.floor(Date.now() / 1000);
    const bufferTime = 60; // 60 seconds buffer
    
    return expiry <= (now + bufferTime);
};

/**
 * Authenticate with the API using fixed credentials
 * The credentials are configured in environment variables
 * @returns {Promise<Object>} Authentication result
 */
export const login = async () => {
    const config = getConfig();
    const API_BASE_URL = config.apiBaseUrl;
    
    // Get credentials from config
    const username = config.apiUsername;
    const password = config.apiPassword;
    
    if (!username || !password) {
        console.error('API credentials not configured');
        return {
            success: false,
            error: 'Credenciales de API no configuradas'
        };
    }
    
    console.log(`API Auth Request to: ${API_BASE_URL}/auth/login`);
    
    try {
        const response = await fetch(`${API_BASE_URL}/auth/login`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                username: username,
                password: password
            })
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('Authentication failed:', errorData);
            return {
                success: false,
                error: errorData.message || `Error de autenticación: ${response.status}`
            };
        }
        
        const data = await response.json();
        console.log('Authentication successful');
        
        // Store tokens
        if (data.AccessToken) {
            sessionStorage.setItem(ACCESS_TOKEN_KEY, data.AccessToken);
        }
        if (data.RefreshToken) {
            sessionStorage.setItem(REFRESH_TOKEN_KEY, data.RefreshToken);
        }
        
        return {
            success: true,
            data: {
                accessToken: data.AccessToken,
                refreshToken: data.RefreshToken
            }
        };
        
    } catch (error) {
        console.error('Error during authentication:', error);
        return {
            success: false,
            error: error.message || 'Error al autenticar con el API'
        };
    }
};

/**
 * Refresh the access token using the refresh token
 * @returns {Promise<Object>} Token refresh result
 */
export const refreshToken = async () => {
    const config = getConfig();
    const API_BASE_URL = config.apiBaseUrl;
    const storedRefreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
    
    if (!storedRefreshToken) {
        console.error('No refresh token available');
        return {
            success: false,
            error: 'No hay token de refresco disponible'
        };
    }
    
    console.log(`API Token Refresh Request to: ${API_BASE_URL}/auth/refresh`);
    
    try {
        const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                refreshToken: storedRefreshToken
            })
        });
        
        if (!response.ok) {
            // If refresh fails, clear tokens and require re-login
            clearTokens();
            console.error('Token refresh failed');
            return {
                success: false,
                error: 'Token refresh failed',
                requiresReauth: true
            };
        }
        
        const data = await response.json();
        console.log('Token refreshed successfully');
        
        // Store new tokens
        if (data.AccessToken) {
            sessionStorage.setItem(ACCESS_TOKEN_KEY, data.AccessToken);
        }
        if (data.RefreshToken) {
            sessionStorage.setItem(REFRESH_TOKEN_KEY, data.RefreshToken);
        }
        
        return {
            success: true,
            data: {
                accessToken: data.AccessToken,
                refreshToken: data.RefreshToken
            }
        };
        
    } catch (error) {
        console.error('Error refreshing token:', error);
        clearTokens();
        return {
            success: false,
            error: error.message || 'Error al refrescar el token',
            requiresReauth: true
        };
    }
};

/**
 * Get the current access token
 * @returns {string|null} Access token or null if not available
 */
export const getAccessToken = () => {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY);
};

/**
 * Get the current refresh token
 * @returns {string|null} Refresh token or null if not available
 */
export const getRefreshToken = () => {
    return sessionStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Check if the user is authenticated (has an access token)
 * @returns {boolean} True if authenticated
 */
export const isAuthenticated = () => {
    return !!getAccessToken();
};

/**
 * Clear all stored tokens
 */
export const clearTokens = () => {
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
};

/**
 * Logout - clear tokens
 */
export const logout = () => {
    clearTokens();
};

/**
 * Initialize authentication on app startup
 * Attempts to login with configured credentials
 * @returns {Promise<Object>} Authentication result
 */
export const initializeAuth = async () => {
    // Check if already authenticated
    if (isAuthenticated()) {
        console.log('Already authenticated');
        return { success: true };
    }
    
    console.log('Initializing authentication...');
    return await login();
};

/**
 * Create authenticated fetch headers
 * Includes the Bearer token if available
 * @param {Object} additionalHeaders - Additional headers to include
 * @returns {Object} Headers object with authentication
 */
export const getAuthHeaders = (additionalHeaders = {}) => {
    const token = getAccessToken();
    const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    };
    
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Merge with additional headers, allowing them to override defaults
    return { ...headers, ...additionalHeaders };
};

/**
 * Wrapper for fetch that includes authentication and handles 401 errors
 * Automatically retries once with refreshed token on 401
 * @param {string} url - URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
export const authenticatedFetch = async (url, options = {}) => {
    // Ensure we're authenticated
    if (!isAuthenticated()) {
        const authResult = await initializeAuth();
        if (!authResult.success) {
            throw new Error('Error al autenticar');
        }
    }
    
    // Check if token is expired or about to expire before making the request
    const currentToken = getAccessToken();
    if (currentToken && isTokenExpiredOrExpiring(currentToken)) {
        console.log('Token expired or expiring soon, refreshing proactively...');
        const refreshResult = await refreshToken();
        
        if (!refreshResult.success) {
            // If refresh failed, try full re-authentication
            console.log('Token refresh failed, attempting full re-authentication...');
            const authResult = await login();
            if (!authResult.success) {
                throw new Error('Error al reautenticar');
            }
        }
    }
    
    // Add auth headers
    const headers = getAuthHeaders(options.headers || {});
    const fetchOptions = {
        ...options,
        headers
    };
    
    // Make the request
    let response = await fetch(url, fetchOptions);
    
    // Handle 401 - try to refresh token once (fallback for cases where proactive refresh didn't happen)
    if (response.status === 401) {
        console.log('Received 401, attempting token refresh...');
        const refreshResult = await refreshToken();
        
        if (refreshResult.success) {
            // Retry the request with new token
            const newHeaders = getAuthHeaders(options.headers || {});
            const retryOptions = {
                ...options,
                headers: newHeaders
            };
            response = await fetch(url, retryOptions);
        } else if (refreshResult.requiresReauth) {
            // Refresh token failed, try full re-authentication
            const authResult = await login();
            if (authResult.success) {
                // Retry the request with new token
                const newHeaders = getAuthHeaders(options.headers || {});
                const retryOptions = {
                    ...options,
                    headers: newHeaders
                };
                response = await fetch(url, retryOptions);
            }
        }
    }
    
    return response;
};
